/**
 * The tenant's wall clock, on the till.
 *
 * WHY THIS EXISTS: the POS evaluates three schedules client-side — menu periods
 * (`menu-period.service.ts`), combo availability windows (`combos.service.ts`)
 * and shift lateness (`OpenShiftPage.tsx`). All three read `new Date()` with the
 * browser's local getters, so the answer depended on the till's own clock. Today
 * that agrees with the server only because every till is in India; a Dubai
 * restaurant running a laptop still set to IST would switch from breakfast to
 * lunch 1.5 hours early, and disagree with the server that priced the order.
 *
 * The zone comes from the tenant, served on `/auth/me` and cached offline in
 * `app_config` beside the currency. A store in another country carries its own
 * zone (T14: an Oman tenant's India store runs on IST), served per location on
 * the same `/auth/me`; the till's active store decides which one applies.
 *
 * Why a module-level value rather than a hook: the schedule helpers are pure
 * synchronous functions called during render and inside `useMemo`, and every
 * existing caller and test depends on that. The auth store sets this once at
 * login, and the cached copy restores it on an offline boot. Same shape as
 * `discount-caps.ts`.
 */
import { DEFAULT_TIMEZONE, isSupportedTimezone, wallClockIn } from '@bbpos/types';
import { useLocationStore } from '@/stores/use-location-store';

let tenantTimezone: string = DEFAULT_TIMEZONE;
/** Stores with their own zone; a store not listed follows the tenant. */
let locationTimezones: Record<string, string> = {};

/**
 * Record the tenant's zone. An unknown or missing value falls back to IST,
 * which is what every till evaluated against before tenants had a zone — an
 * older API that does not serve the field must not change behaviour.
 */
export function setTenantTimezone(timezone: string | null | undefined): void {
  tenantTimezone = timezone && isSupportedTimezone(timezone) ? timezone : DEFAULT_TIMEZONE;
}

export function getTenantTimezone(): string {
  return tenantTimezone;
}

/** Record each store's own zone from `/auth/me` `locations[]`; null or unknown = the tenant's. */
export function setLocationTimezones(
  locations: ReadonlyArray<{ id: string; timezone?: string | null }> | null | undefined,
): void {
  locationTimezones = {};
  for (const l of locations ?? []) {
    if (l.timezone && isSupportedTimezone(l.timezone)) locationTimezones[l.id] = l.timezone;
  }
}

/** The zone of `locationId` (default: the till's active store), else the tenant's. */
export function getLocationTimezone(
  locationId: string | null | undefined = useLocationStore.getState().currentLocationId,
): string {
  return (locationId && locationTimezones[locationId]) || tenantTimezone;
}

/**
 * `instant` re-expressed as the store's wall clock: a Date whose LOCAL getters
 * read the business's clock rather than this browser's. The store is
 * `locationId`, default the till's active one; without its own zone, the
 * tenant's. Not a valid instant — only its calendar and clock fields are
 * meaningful, so never store or send it.
 */
export function tenantNow(instant: Date = new Date(), locationId?: string | null): Date {
  return wallClockIn(getLocationTimezone(locationId), instant);
}

/**
 * Date-range presets on the store's calendar (T18): "Today" is the store's day,
 * not this device's. Each date is a local midnight whose fields are the store's
 * date — what the DatePicker shows and `formatDateParam` sends. `null` = all
 * stores, which the API reads on the tenant's zone, so the presets do too.
 */
export function storeDatePresets(
  locationId: string | null,
  instant: Date = new Date(),
): Array<{ label: string; value: { from: Date; to: Date } }> {
  const clock = wallClockIn(getLocationTimezone(locationId), instant);
  const day = (offset: number) => new Date(clock.getFullYear(), clock.getMonth(), clock.getDate() + offset);
  const today = day(0);
  return [
    { label: 'Today', value: { from: today, to: today } },
    { label: 'Yesterday', value: { from: day(-1), to: day(-1) } },
    { label: 'Last 7 days', value: { from: day(-6), to: today } },
    { label: 'This month', value: { from: new Date(today.getFullYear(), today.getMonth(), 1), to: today } },
    {
      label: 'Last month',
      value: {
        from: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        to: new Date(today.getFullYear(), today.getMonth(), 0),
      },
    },
  ];
}
